import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import ModuleSearchControls, {
  ModuleFilterMenu,
  ModulePerPageMenu,
  ModuleRadioOption,
  ModuleSortMenu,
} from "../../components/ModuleSearchControls";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import PageAlert from "../../components/PageAlert/PageAlert";
import DataTable, { DataTableActions, DataTableFooter } from "../../components/DataTable";
import ArticlesNav from "./components/ArticlesNav";
import { resolveApiError } from "../../utils/apiError";
import {
  deleteArticleMedia,
  getArticleMedia,
  getArticleMediaUploadError,
  getMediaUrl,
  isImageMedia,
  normalizePaginated,
  uploadArticleMedia,
  validateArticleMediaFile,
} from "./articleApi";
import "./articles.scss";

const SORT_OPTIONS = [
  { value: "created_at", label: "Created date" },
  { value: "file_name", label: "File name" },
  { value: "size", label: "Size" },
  { value: "mime_type", label: "Type" },
];

const formatSize = (bytes) => {
  if (!bytes) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const ArticleMedia = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [fileType, setFileType] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [mediaItems, setMediaItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [sortOpen, setSortOpen] = useState(false);
  const [perPageOpen, setPerPageOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  const params = useMemo(
    () => ({
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_order: sortOrder,
      keyword: debouncedKeyword || undefined,
      type: fileType === "all" ? undefined : fileType,
    }),
    [page, perPage, sortBy, sortOrder, debouncedKeyword, fileType]
  );

  const loadMedia = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getArticleMedia(params);
      const result = normalizePaginated(response, { per_page: perPage });
      setMediaItems(result.items);
      setMeta(result.meta);
      setSelectedMedia((current) => {
        if (current && result.items.some((item) => item.id === current.id)) {
          return result.items.find((item) => item.id === current.id) || current;
        }
        return result.items[0] || null;
      });
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setLoading(false);
    }
  }, [params, perPage, navigate]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const handleUpload = async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []).filter((file) => file && file.size > 0);
    if (!files.length) return;

    const invalidMessage = files.map((file) => validateArticleMediaFile(file, { allowPdf: true })).find(Boolean);
    if (invalidMessage) {
      setError(invalidMessage);
      input.value = "";
      return;
    }

    setUploading(true);
    setError("");
    setNotice("");

    try {
      let latest = null;
      for (const file of files) {
        latest = await uploadArticleMedia({ file, alt_text: file.name, caption: "" });
      }
      setNotice(`${files.length} media file${files.length === 1 ? "" : "s"} uploaded.`);
      await loadMedia();
      if (latest) setSelectedMedia(latest);
    } catch (err) {
      const uploadError = getArticleMediaUploadError(err);
      if (uploadError) setError(uploadError);
      else resolveApiError(err, setError, navigate);
    } finally {
      setUploading(false);
      input.value = "";
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteArticleMedia(pendingDelete.id);
      setPendingDelete(null);
      setNotice("Media deleted.");
      if (selectedMedia?.id === pendingDelete.id) setSelectedMedia(null);
      await loadMedia();
    } catch (err) {
      resolveApiError(err, setError, navigate);
    } finally {
      setDeleting(false);
    }
  };

  const resetControls = () => {
    setKeyword("");
    setDebouncedKeyword("");
    setFileType("all");
    setSortBy("created_at");
    setSortOrder("desc");
    setPerPage(20);
    setPage(1);
  };

  const previewUrl = selectedMedia && isImageMedia(selectedMedia) ? getMediaUrl(selectedMedia) : "";

  return (
    <div className="articles-page">
      {notice && (
        <PageAlert variant="success" onDismiss={() => setNotice("")}>{notice}</PageAlert>
      )}
      {error && <PageAlert variant="error" onDismiss={() => setError("")}>{error}</PageAlert>}

      <div className="articles-page__heading">
        <div>
          <h2>Media library</h2>
          <p>Upload and manage images used in article content. JPG, PNG, WebP or PDF, max 5 MB.</p>
        </div>
        <button
          className="ui-button ui-button--primary"
          type="button"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? "Uploading..." : <><Icon icon="ph:plus" height={14} /> Upload media</>}
        </button>
        <input
          ref={fileInputRef}
          hidden
          multiple
          type="file"
          accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp,application/pdf,.pdf"
          onChange={handleUpload}
        />
      </div>

      <ArticlesNav />

      <section className="articles-page__media-layout">
        <div className="articles-page__card">
          <ModuleSearchControls value={keyword} onChange={setKeyword} placeholder="Search media">
            <ModuleFilterMenu
              open={filterOpen}
              onToggle={() => {
                setFilterOpen((open) => !open);
                setSortOpen(false);
                setPerPageOpen(false);
              }}
              onClose={() => setFilterOpen(false)}
              count={fileType === "all" ? 0 : 1}
              columns={1}
              size="narrow"
              ariaLabel="Media filters"
            >
              <fieldset>
                <legend>Type</legend>
                {[
                  ["all", "All files"],
                  ["image", "Images"],
                  ["pdf", "PDF"],
                ].map(([value, label]) => (
                  <ModuleRadioOption
                    key={value}
                    name="article-media-type"
                    checked={fileType === value}
                    onChange={() => {
                      setFileType(value);
                      setPage(1);
                    }}
                    plain
                  >
                    {label}
                  </ModuleRadioOption>
                ))}
              </fieldset>
            </ModuleFilterMenu>
            <ModuleSortMenu
              open={sortOpen}
              onToggle={() => {
                setSortOpen((open) => !open);
                setFilterOpen(false);
                setPerPageOpen(false);
              }}
              onClose={() => setSortOpen(false)}
              sortBy={sortBy}
              sortOrder={sortOrder}
              options={SORT_OPTIONS}
              onChangeSortBy={(value) => {
                setSortBy(value);
                setPage(1);
              }}
              onChangeSortOrder={(value) => {
                setSortOrder(value);
                setPage(1);
              }}
            />
            <ModulePerPageMenu
              open={perPageOpen}
              onToggle={() => {
                setPerPageOpen((open) => !open);
                setFilterOpen(false);
                setSortOpen(false);
              }}
              onClose={() => setPerPageOpen(false)}
              value={perPage}
              options={[10, 20, 50]}
              onChange={(value) => {
                setPerPage(value);
                setPage(1);
              }}
            />
            <button className="ui-button" type="button" onClick={resetControls}>
              <Icon icon="ph:arrow-clockwise" height={14} /> Reset
            </button>
          </ModuleSearchControls>

          <DataTable
            loading={loading}
            loadingMessage="Loading media..."
            emptyMessage="No media uploaded yet."
            minWidth={560}
            rows={mediaItems}
            onRowClick={setSelectedMedia}
            rowClassName={(item) => (selectedMedia?.id === item.id ? "is-selected" : "")}
            columns={[
              {
                key: "file_name",
                header: "File",
                render: (item) => {
                  const url = isImageMedia(item) ? getMediaUrl(item) : "";
                  return (
                    <div className="articles-page__media-file">
                      {url ? <img src={url} alt="" /> : <span>PDF</span>}
                      <div>
                        <strong>{item.file_name || "Untitled file"}</strong>
                        <small>{item.alt_text || "No alt text"}</small>
                      </div>
                    </div>
                  );
                },
              },
              {
                key: "mime_type",
                header: "Type",
                render: (item) => item.mime_type || "—",
              },
              {
                key: "size",
                header: "Size",
                render: (item) => formatSize(item.size),
              },
              {
                key: "actions",
                header: "Actions",
                render: (item) => (
                  <DataTableActions>
                    <button
                      type="button"
                      className="danger"
                      onClick={(event) => {
                        event.stopPropagation();
                        setPendingDelete(item);
                      }}
                    >
                      Delete
                    </button>
                  </DataTableActions>
                ),
              },
            ]}
          />
          <DataTableFooter meta={meta} page={page} loading={loading} onPageChange={setPage} noun="files" />
        </div>

        <aside className="articles-page__media-detail articles-page__card">
          {!selectedMedia && <div className="articles-page__empty">Select a file to preview.</div>}
          {selectedMedia && previewUrl && <img src={previewUrl} alt="" />}
          {selectedMedia && !previewUrl && (
            <div className="articles-page__empty">No image preview.</div>
          )}
        </aside>
      </section>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete media"
        message={pendingDelete ? `Delete “${pendingDelete.file_name}”? This cannot be undone.` : ""}
        confirming={deleting}
        onCancel={() => !deleting && setPendingDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ArticleMedia;
